import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@parkchirp/api-client';
import { GET_FACILITY_AREAS_BRANCHES_DISTRICTS_V2 } from '../../queries';
import type { FacilityHierarchyItem } from '../../types';
import { FacilityFilter, type FacilityOption } from './FacilityFilter';
import type { OrganizationOption } from './OrganizationFilter';
import { OrganizationsFilter } from './OrganizationsFilter';
import { ReportingMultiSelect } from './ReportingMultiSelect';

export type FacilityHierarchyFilterValue = {
	areas: FacilityHierarchyItem[];
	branches: FacilityHierarchyItem[];
	districts: FacilityHierarchyItem[];
	organizations?: OrganizationOption[];
	facilities: FacilityOption[];
};

type HierarchyResponse = {
	listFacilityAreasBranchesDistricts: {
		facilityAreas: FacilityHierarchyItem[];
		facilityBranches: FacilityHierarchyItem[];
		facilityDistricts: FacilityHierarchyItem[];
	};
};

type Props = {
	organizationId?: number | null;
	showOrganizations?: boolean;
	value: FacilityHierarchyFilterValue;
	onChange: (value: FacilityHierarchyFilterValue) => void;
};

const emptyHierarchyOptions: HierarchyResponse['listFacilityAreasBranchesDistricts'] = {
	facilityAreas: [],
	facilityBranches: [],
	facilityDistricts: [],
};

export const FacilityHierarchyFilter = ({ organizationId, showOrganizations, value, onChange }: Props) => {
	const { data, loading } = useQuery<HierarchyResponse>(GET_FACILITY_AREAS_BRANCHES_DISTRICTS_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
	});

	const options = data?.listFacilityAreasBranchesDistricts ?? emptyHierarchyOptions;

	const updateValue = (changes: Partial<FacilityHierarchyFilterValue>) => {
		onChange({
			...value,
			...changes,
		});
	};

	return (
		<Stack spacing={2}>
			<Typography variant='subtitle1' fontWeight={600}>
				Facility filters
			</Typography>

			<Box
				sx={{
					display: 'grid',
					gap: 2,
					gridTemplateColumns: {
						xs: '1fr',
						md: 'repeat(2, minmax(0, 1fr))',
					},
				}}
			>
				<ReportingMultiSelect
					label='Areas'
					loading={loading}
					options={options.facilityAreas}
					value={value.areas}
					getOptionId={(option) => option.id}
					getOptionLabel={(option) => option.name}
					onChange={(areas) => {
						updateValue({ areas });
					}}
				/>

				<ReportingMultiSelect
					label='Branches'
					loading={loading}
					options={options.facilityBranches}
					value={value.branches}
					getOptionId={(option) => option.id}
					getOptionLabel={(option) => option.name}
					onChange={(branches) => {
						updateValue({ branches });
					}}
				/>

				<ReportingMultiSelect
					label='Districts'
					loading={loading}
					options={options.facilityDistricts}
					value={value.districts}
					getOptionId={(option) => option.id}
					getOptionLabel={(option) => option.name}
					onChange={(districts) => {
						updateValue({ districts });
					}}
				/>

				<FacilityFilter
					organizationId={organizationId}
					value={value.facilities}
					onChange={(facilities) => {
						updateValue({ facilities });
					}}
				/>

				{showOrganizations && (
					<OrganizationsFilter
						value={value.organizations ?? []}
						onChange={(organizations) => {
							updateValue({ organizations });
						}}
					/>
				)}
			</Box>
		</Stack>
	);
};
